import { useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import {
  GET_ORGANIZATION_ROADMAP_NODES_PATH,
  GetOrganizationRoadmapNodesErrorDto,
  GetOrganizationRoadmapNodesSuccessDto,
} from '@/api/rest/resources/types/organization';
import { assertIsDefined } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';

import { useOrganizationId } from './useOrganizationId';

type SuccessDto = GetOrganizationRoadmapNodesSuccessDto;
type ErrorDto = GetOrganizationRoadmapNodesErrorDto;

export const useOrganizationRoadmapNodes = (organizationIdArg?: string, filters?: Record<string, unknown> | null) => {
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;

  assertIsDefined(organizationId, 'organizationId');

  const pathVariables = { organization_id: organizationId };
  const queryParams = { ...filters };

  const queryFn = () => {
    return getRequest<SuccessDto>(GET_ORGANIZATION_ROADMAP_NODES_PATH, pathVariables, queryParams);
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: ['organization', pathVariables, 'roadmap-nodes', queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};

export const useOrganizationRoadmapNode = (roadmapNodeId: string, organizationIdArg?: string) => {
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;

  const roadmapNodes = useOrganizationRoadmapNodes(organizationId).data;

  if (isNullish(roadmapNodeId)) {
    return null;
  }
  return roadmapNodes.find((node) => node.id === roadmapNodeId);
};
